/**
 * Shared hover tooltip for the hand-rolled SVG charts.
 *
 * Port of `ttShow` / `ttHide` from docs/plans/conversations/09-dashboard-mockup.html.
 * Content is plain React children (never `dangerouslySetInnerHTML`) — the lines carry
 * engine/target labels, which are untrusted text.
 *
 * `node` is rendered by the consumer (inside the chart's own wrapper); it is
 * `position: fixed` + `pointer-events: none`, so it never steals a hit target.
 */

import { useCallback, useState } from 'react';
import { Box } from '@mui/material';
import { VIZ } from './tokens';

const W = 240; // clamp width — the box is min-width 160 and wraps at 240
const LINE_H = 19;

export function useVizTooltip() {
  const [tip, setTip] = useState<{ x: number; y: number; lines: string[] } | null>(null);

  const show = useCallback((ev: { clientX: number; clientY: number }, lines: string[]) => {
    setTip({ x: ev.clientX, y: ev.clientY, lines });
  }, []);

  const hide = useCallback(() => setTip(null), []);

  let node: React.ReactNode = null;
  if (tip && tip.lines.length) {
    const h = tip.lines.length * LINE_H + 18;
    const left = Math.min(window.innerWidth - W - 10, Math.max(10, tip.x + 14));
    const top = Math.min(window.innerHeight - h - 10, Math.max(10, tip.y - h / 2));
    node = (
      <Box
        role="tooltip"
        sx={{
          position: 'fixed',
          left,
          top,
          zIndex: 1500,
          pointerEvents: 'none',
          maxWidth: W,
          minWidth: 160,
          bgcolor: VIZ.surface,
          border: '1px solid rgba(0,0,0,0.10)',
          borderRadius: '10px',
          px: 1.4,
          py: 1.1,
          boxShadow: '0 8px 26px rgba(0,0,0,0.14)',
          fontSize: 13,
          lineHeight: `${LINE_H}px`,
          color: VIZ.ink,
        }}
      >
        {tip.lines.map((line, i) => (
          <Box
            key={i}
            sx={{
              color: i === 0 ? VIZ.ink2 : VIZ.ink,
              fontSize: i === 0 ? 12 : 13,
              fontWeight: i === 0 ? 400 : 500,
            }}
          >
            {line}
          </Box>
        ))}
      </Box>
    );
  }

  return { show, hide, node };
}

export type VizTooltip = ReturnType<typeof useVizTooltip>;
